import { useCallback, useRef } from "react";
import i18n from 'i18next';
import { extractClassModeText } from './useWsMessageHandlers';

const useWsExpertDialogHandler = ({
  setFiles,
  setExpertFlashMap,
  setExpertUnreadMap,
  expertFlashTimersRef,
  isRestoringArchiveRef,
  activeFile,
  setActiveFile,
  fileContentExpanded,
  setFileContentExpanded,
  setChatPanelWidth,
  digitalHumanGlobalEnabledRef,
  isExpertServiceAvailableRef,
  currentInitializingExpertRef,
  classModeRef,
  setCurrentFileExpertText,
  setCurrentFileExpertName,
  setFileExpertDigitalHumanOpen,
  onCCDialogActivated,
}) => {
  // 本轮流式开始前的文件正文，供 replace_streamed 完成包覆盖暂存内容，避免重复追加。
  const streamBaseByFileRef = useRef({});

  return useCallback((data) => {
    if (data.type !== "expert_dialog_update") return false;

    // 按 source 隔离：
    //   source="youlaber" → 来自顶部独立 YouLaber tab，仅由 YouLaberPanel 自己消费，
    //                       绝不镜像到综合训练的文件面板，也不切 tab。
    //   source="ccma"/缺省 → 综合训练入口（含 @YouLaber 旧入口），保留原文件面板/CCPanel 行为。
    if (data.source === "youlaber") {
      return true;
    }

    const expertFileId = data.file_id;
    const expertName = data.expert_name;
    const dialogContent = data.dialog_content;

    const isStreamingChunk = !!data.is_streaming_chunk;
    const isStreamingDone = !!data.is_streaming_done;
    const replaceStreamed = !!data.replace_streamed;

    setFiles((prevFiles) => {
      const existingFile = prevFiles[expertFileId];

      if (existingFile) {
        let updatedContent;

        if (isStreamingChunk) {
          if (!existingFile.meta?.isStreaming) {
            streamBaseByFileRef.current[expertFileId] = existingFile.content;
          }
          updatedContent = existingFile.content + dialogContent;
        } else if (isStreamingDone && replaceStreamed) {
          // 流式完成：用完整本轮内容覆盖暂存，避免 chunk 与 done 重复。
          const base = streamBaseByFileRef.current[expertFileId];
          delete streamBaseByFileRef.current[expertFileId];
          if (base !== undefined) {
            updatedContent = base + dialogContent;
          } else {
            // 无基线时无法可靠剥离暂存；保留当前正文，不再追加完整本轮。
            updatedContent = existingFile.content;
          }
        } else {
          if (isStreamingDone) {
            delete streamBaseByFileRef.current[expertFileId];
          }
          updatedContent = existingFile.content + dialogContent;
        }

        console.log("专家对话更新 - 文件已存在，更新内容:", expertFileId, {
          isStreamingChunk,
          isStreamingDone,
          replaceStreamed,
        });
        // 合并 meta：is_cc_device / is_claw_dialog 可能在后续消息中升级（如存档恢复后发起新对话）
        const newIsCCDevice = !!data.is_cc_device || !!existingFile.meta.isCCDevice;
        const newIsClawDialog = !!data.is_claw_dialog || !!existingFile.meta.isClawDialog;
        return {
          ...prevFiles,
          [expertFileId]: {
            ...existingFile,
            content: updatedContent,
            meta: {
              ...existingFile.meta,
              lastUpdated: new Date().getTime(),
              isStreaming: isStreamingChunk ? true : (isStreamingDone ? false : existingFile.meta.isStreaming),
              isCCDevice: newIsCCDevice,
              isClawDialog: newIsClawDialog,
              // expert_cmd 也随最新消息更新（存档恢复后可能为空）
              expert_cmd: data.expert_cmd || existingFile.meta.expert_cmd,
            },
          },
        };
      }

      console.log("专家对话更新 - 创建新文件:", expertFileId);
      const dialogTitle = i18n.t('expert_dialog_title_format', { name: expertName });
      const header = `# 💬 ${dialogTitle}

> ${i18n.t('expert_dialog_history_note', { name: expertName })}  

`;
      if (isStreamingChunk) {
        streamBaseByFileRef.current[expertFileId] = header;
      }
      const dialogFullContent = `${header}${dialogContent}`;
      const expertDialogFile = {
        content: dialogFullContent,
        originalContent: dialogFullContent,
        meta: {
          filename: expertName,
          role: expertName,
          originalFilename: `${expertName}_${i18n.t('expert_dialog_file_suffix')}.final`,
          timestamp: new Date().getTime(),
          version: 1,
          expert_cmd: data.expert_cmd,
          expert_name: expertName,
          isExpertDialog: true,
          isClawDialog: !!data.is_claw_dialog,
          isCCDevice: !!data.is_cc_device,
          isStreaming: isStreamingChunk,
        },
      };
      return {
        ...prevFiles,
        [expertFileId]: expertDialogFile,
      };
    });

    if (
      data.is_cc_device &&
      !isRestoringArchiveRef.current &&
      !isStreamingChunk &&
      !isStreamingDone &&
      onCCDialogActivated
    ) {
      onCCDialogActivated(expertFileId);
    }

    try {
      if (expertFlashTimersRef.current[expertFileId]) {
        clearTimeout(expertFlashTimersRef.current[expertFileId]);
      }
      setExpertFlashMap((prev) => ({ ...prev, [expertFileId]: true }));
      expertFlashTimersRef.current[expertFileId] = setTimeout(() => {
        setExpertFlashMap((prev) => {
          const next = { ...prev };
          delete next[expertFileId];
          return next;
        });
        delete expertFlashTimersRef.current[expertFileId];
      }, 3000);
      // 非当前活动文件时置未读小红点（activeFile 切换 useEffect 会负责清除）
      if (setExpertUnreadMap && activeFile && activeFile !== expertFileId) {
        setExpertUnreadMap((prev) => ({ ...prev, [expertFileId]: true }));
      }
    } catch (e) {
      console.warn("设置专家对话高亮失败:", e);
    }

    console.log(`📌 检查恢复模式: isRestoringArchiveRef.current = ${isRestoringArchiveRef.current}`);
    if (!isRestoringArchiveRef.current) {
      if (!activeFile || activeFile === expertFileId) {
        console.log("🎯 自动切换到专家对话文件:", expertFileId);
        setActiveFile(expertFileId);
        if (!fileContentExpanded) {
          setFileContentExpanded(true);
          setChatPanelWidth(38.2);
        }
      }
    } else {
      console.log("🔄 恢复模式：跳过自动切换专家对话，fileId:", expertFileId);
    }

    if (data.expert_name && dialogContent && !data.is_streaming_chunk) {
      if (isRestoringArchiveRef.current) {
        console.log("⏸️ 存档恢复中，跳过文件面板数字人触发:", data.expert_name);
      } else if (!digitalHumanGlobalEnabledRef.current) {
        console.log("🔇 数字人全局开关已关闭，跳过文件面板触发:", data.expert_name);
      } else if (isExpertServiceAvailableRef.current) {
        // 先从 dialogContent 中提取专家回复正文（去掉"**专家回复**"标记行本身）
        let textToSpeak = dialogContent;
        const replyMarker = i18n.t('expert_reply_marker');
        const replyIndex = dialogContent.indexOf(replyMarker);

        if (replyIndex !== -1) {
          const afterReply = dialogContent.substring(replyIndex);
          const firstNewline = afterReply.indexOf("\n");
          if (firstNewline !== -1) {
            textToSpeak = afterReply.substring(firstNewline + 1).trim();
          }
        }

        if (classModeRef.current) {
          // 上课模式优先使用后端提供的正文（流式结束时下发），其次再本地清洗
          const speechText = typeof data.speech_text === "string" ? data.speech_text.trim() : "";
          if (speechText) {
            textToSpeak = speechText;
          } else {
            textToSpeak = extractClassModeText(textToSpeak);
          }
        } else {
          // 非上课模式优先使用 <voice> 标签内容（流式场景下会提前下发）
          const voiceMatch = textToSpeak.match(/<voice>([\s\S]*?)<\/voice>/i);
          if (voiceMatch && voiceMatch[1]) {
            textToSpeak = voiceMatch[1].trim();
          }
        }

        if (currentInitializingExpertRef.current === data.expert_name) {
          console.log("🎤 [预初始化完成] 专家已预加载，直接更新回复文本:", data.expert_name);
          setCurrentFileExpertText(textToSpeak);
          currentInitializingExpertRef.current = null;
        } else {
          console.log("🎭 检测到专家回复，触发文件面板数字人:", data.expert_name);
          console.log("🔍 数字人服务可用性检查 (文件面板):", isExpertServiceAvailableRef.current);
          setCurrentFileExpertName(data.expert_name);
          setCurrentFileExpertText(textToSpeak);
          setFileExpertDigitalHumanOpen(true);
        }
      } else {
        console.log("⚠️ 数字人服务不可用，跳过弹窗 (ref值:", isExpertServiceAvailableRef.current, ")");
      }
    }

    return true;
  }, [
    activeFile,
    classModeRef,
    currentInitializingExpertRef,
    digitalHumanGlobalEnabledRef,
    expertFlashTimersRef,
    fileContentExpanded,
    isExpertServiceAvailableRef,
    isRestoringArchiveRef,
    setActiveFile,
    setChatPanelWidth,
    setCurrentFileExpertName,
    setCurrentFileExpertText,
    onCCDialogActivated,
    setExpertFlashMap,
    setExpertUnreadMap,
    setFileContentExpanded,
    setFileExpertDigitalHumanOpen,
    setFiles,
  ]);
};

export default useWsExpertDialogHandler;
